import React, { useState, useEffect } from 'react'
import { validationColors, cities } from '../assets/data';
import { useUpdateUserMutation, useGetUserByIdQuery } from '../features/users/usersApiSlice';
import InputBox from './InputBox';
import TextArea from './TextArea';
import DropdownInput from './DropdownInput';

import { Button } from "./ui/button";
import useAuth from '../hooks/useAuth';

import { IoCloseOutline } from "react-icons/io5";
import { MdModeEdit } from "react-icons/md";

const EMAIL_INPUT_VALIDATOR_REGEX =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const STRING_INPUT_VALIDATOR_REGEX = /^[A-Za-z ]{3,20}$/;
const PHONE_NUMBER_INPUT_VALIDATOR_REGEX = /^[0-9]{10}$/;
// const PHONE_NUMBER_INPUT_VALIDATOR_REGEX = /^(07|09)[0-9]{8}$/;

const UpdateProfile = () => {
  const { id } = useAuth();
  const { data: user } = useGetUserByIdQuery(id);
  const [updateUser, { isSuccess }] = useUpdateUserMutation();

  const [edit, setEdit] = useState(false);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [bio, setBio] = useState("");
  // const [city, setCity] = useState("");

  const [validEmail, setValidEmail] = useState(false);
  const [validPhoneNumber, setValidPhoneNumber] = useState(false);
  const [validCity, setValidCity] = useState(false);
  const [validFullName, setValidFullName] = useState(false);

  useEffect(() => {setFullName(user?.firstName + " " + user?.lastName);}, [user]);
  useEffect(() => {setEmail(user?.email);}, [user]);
  useEffect(() => {setPhoneNumber(user?.phoneNumber);}, [user]);
  useEffect(() => {setBio(user?.bio || "");}, [user]);
  // useEffect(() => {setCity(user?.city);}, [user]);
  
  useEffect(() => {
    setValidFullName(STRING_INPUT_VALIDATOR_REGEX.test(fullName));
  }, [fullName]);

  useEffect(() => {
    setValidEmail(EMAIL_INPUT_VALIDATOR_REGEX.test(email));
  }, [email]);

  useEffect(() => {
    setValidPhoneNumber(PHONE_NUMBER_INPUT_VALIDATOR_REGEX.test(phoneNumber));
  }, [phoneNumber]);


  const onFullNameChanged = (e) => setFullName(e.target.value);
  const onEmailChanged = (e) => setEmail(e.target.value);
  const onPhoneNumberChanged = (e) => setPhoneNumber(e.target.value);
  const onBioChanged = (e) => setBio(e.target.value);
  // const onCityChanged = (e) => setCity(e.target.value);

  const canSave =
    [
      validFullName,
      validEmail,
      validPhoneNumber,
    ].every(Boolean);


  const onUpdateInfoClicked = async (e) => {
    e.preventDefault();
    if (canSave) {
      await updateUser({id, fullName, email, phoneNumber, bio});
    }
  }
  useEffect(() => {
    if (isSuccess) {
      setEdit(false);
    }
  }, [isSuccess]);

  function formatPhoneNumber(phone) {
    const countryCode = "+1";
    const part1 = phone.substring(0, 3);
    const part2 = phone.substring(3, 6);
    const part3 = phone.substring(6);

    // Combine the parts with the desired formatting
    return `${countryCode}-(${part1})-${part2}-${part3}`;
  }

  const validFullNameClass = !validFullName && fullName !== "" ? validationColors.bgInvalid : "";
  const validEmailClass = !validEmail && email !== "" ? validationColors.bgInvalid : "";
  const validPhoneNumberClass = !validPhoneNumber && phoneNumber !== "" ? validationColors.bgInvalid : "";
  // const validCityClass = !validCity && city !== "" ? validationColors.bgInvalid : "";
  
  let content;
  if(user) {
    const formattedPhone = formatPhoneNumber(user.phoneNumber);
    content = (
      edit === true ? (
      <form 
        className='relative w-full max-w-2xl flex flex-col justify-start items-start gap-2 bg-white py-7 px-10 rounded-md shadow-md border border-slate-200'
        onSubmit={onUpdateInfoClicked}
      >
        <div className="w-full flex justify-between items-center mb-2">
          <p className='font-semibold text-[1.3rem] text-slate-800'>Edit Profile</p>
        </div>
        <span 
          className='text-[1.3rem] cursor-pointer text-slate-500/90 absolute top-3 right-3 hover:text-slate-800 transition-colors' 
          title='Cancel Edit' 
          onClick={() => {setEdit(false)}}
        >
          <IoCloseOutline />
        </span>

        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputBox 
            usedFor="fullName" 
            boxLabel="Name and Surname" 
            value={fullName} 
            login={true} 
            onChange={onFullNameChanged} 
            validationClass={validFullNameClass} 
            mustFill={true}
            className="w-full"
          />
          <InputBox 
            usedFor="email" 
            boxLabel="Email" 
            value={email} 
            login={true} 
            onChange={onEmailChanged} 
            validationClass={validEmailClass} 
            mustFill={true}
            className="w-full"
          />
          <InputBox 
            usedFor="phone" 
            boxLabel="Phone Number" 
            value={phoneNumber} 
            login={true} 
            onChange={onPhoneNumberChanged} 
            validationClass={validPhoneNumberClass} 
            mustFill={true}
            className="w-full"
          />
        </div>

        <TextArea
          label="Bio"
          value={bio}
          onChange={onBioChanged}
          maxLength={200}
          placeholder="Tell us about yourself..."
          className="w-full mt-2"
        />

        <Button 
          variant="outline" 
          disabled={!canSave} 
          className="w-full text-white hover:text-white bg-gradient-to-r from-blue-500 to-blue-600 h-[50px] text-[1.2rem] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          Save Changes
        </Button>
      </form>
      ): (
        <div className='flex flex-col justify-start items-start rounded-md py-7 px-10 shadow-md bg-white border border-slate-200 select-none relative w-full max-w-2xl'>
          <span
            className="absolute top-3 right-3 m-auto rounded-full text-slate-500/90 hover:text-black cursor-pointer"
            title="Edit information"
            onClick={() => {setEdit(true)}}
          >
            <MdModeEdit />
          </span>

          <p className='font-semibold text-[1.3rem]'>{user.firstName + " " + user.lastName}</p>
          <p className='text-[.8rem] text-blue-500 pl-1 leading-4'>{formattedPhone}</p>
          <p className='text-[.8rem] text-blue-500 underline pl-1 leading-4'>{user.email}</p>
          {user.bio ? (
            <p className='text-[.8rem] mt-4 text-slate-500 pl-3 border-l-2 border-neutral-200'>{user.bio}</p>
          ) : (
            <div className='mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
              <p className='text-amber-700 text-sm'>
                Add a bio to help others understand what you're looking for and what you can offer. 
                Share your interests, expertise, or what you're trying to achieve.
              </p>
            </div>
          )}
        </div>
      )
    );
  }

  return content;
}

export default UpdateProfile